<template>
	<Row>
		<Container>
			<div
				class="bg-gradient-blue-to-pink rounded-3xl p-8 md:p-10 lg:p-14 text-white space-y-8"
			>
				<!-- Title and Description -->
				<div>
					<h2 class="text-5xl lg:text-[56px] font-semibold mb-4 title">
						System Requirements
					</h2>
					<p class="text-base lg:text-lg text-slate-100">
						StocksToTrade needs a decent computer setup to work well. You should have
						a fast internet connection, at least 10Mbps for downloads, ideally 30Mbps
						for the best performance.
					</p>
				</div>

				<div class="flex gap-4 mb-8">
					<div
						class="px-4 py-3 cursor-pointer rounded-xl flex items-center gap-3"
						:class="
							activeTab === 'windows'
								? 'bg-gradient-to-r from-blue-300 to-pink'
								: 'btn-inactive'
						"
						@click="activeTab = 'windows'"
					>
						<img
							:src="
								activeTab === 'windows'
									? '/images/windows-icon-white.svg'
									: '/images/windows-icon-gray.svg'
							"
							class="w-8 h-8"
						/>
						<div
							class="text-lg font-medium hidden md:block"
							:class="activeTab === 'windows' ? 'text-white' : 'text-slate-300'"
						>
							Windows
						</div>
					</div>
					<div
						class="px-4 py-3 cursor-pointer rounded-xl flex items-center gap-3"
						:class="
							activeTab === 'mac'
								? 'bg-gradient-to-r from-blue-300 to-pink'
								: 'btn-inactive'
						"
						@click="activeTab = 'mac'"
					>
						<img
							:src="
								activeTab === 'mac'
									? '/images/apple-icon-white.svg'
									: '/images/apple-icon-gray.svg'
							"
							class="w-8 h-8"
						/>
						<div
							class="text-lg font-medium text-slate-300 hidden md:block"
							:class="activeTab === 'mac' ? 'text-white' : 'text-slate-300'"
						>
							Mac OS
						</div>
					</div>
				</div>

				<div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
					<div class="requirements-card rounded-xl p-6 space-y-6">
						<h3 class="text-2xl font-medium">Minimal</h3>
						<div class="space-y-4">
							<TransitionGroup
								name="list"
								tag="div"
							>
								<CheckmarkItem
									v-for="(item, i) in minimalRequirements"
									:key="i"
									:item="item"
								/>
							</TransitionGroup>
						</div>
					</div>
					<div class="requirements-card rounded-xl p-6 space-y-6">
						<h3 class="text-2xl font-medium">Recommended</h3>
						<div class="space-y-4">
							<TransitionGroup
								name="list"
								tag="div"
							>
								<CheckmarkItem
									v-for="(item, i) in recommendedRequirements"
									:key="i"
									:item="item"
								/>
							</TransitionGroup>
						</div>
					</div>
				</div>
			</div>
		</Container>
	</Row>
</template>

<script setup>
const activeTab = ref("windows");

const windowsMinimal = [
	{
		title: "Processor",
		text: "Quad-core+ CPU 2Ghz+(Intel i5, Intel i7, Intel i9"
	},
	{
		title: "Memory",
		text: "4GB"
	},
	{
		title: "Disk Space",
		text: "200MB"
	},
	{
		title: "Monitor Resolution",
		text: "1440x768"
	},
	{
		title: "System",
		text: "Windows 10"
	}
];

const windowsReccommend = [
	{
		title: "Processor",
		text: "Quad-core + CPU 2Ghz + (Intel i5, Intel i7, Intel i9)"
	},
	{
		title: "Memory",
		text: "8GB"
	},
	{
		title: "Disk Space",
		text: "200MB"
	},
	{
		title: "Monitor Resolution",
		text: "1920x1080"
	},
	{
		title: "System",
		text: "Windows 11"
	}
];

const macMinimal = [
	{
		title: "Processor",
		text: "Quad-core+ CPU 2Ghz+(Intel i5, Intel i7, Intel i9"
	},
	{
		title: "Memory",
		text: "4GB"
	},
	{
		title: "Disk Space",
		text: "200MB"
	},
	{
		title: "Monitor Resolution",
		text: "1440x768"
	},
	{
		title: "System",
		text: "10.15.7 Catalina"
	}
];

const macReccommend = [
	{
		title: "Processor",
		text: "Apple M1 and above"
	},
	{
		title: "Memory",
		text: "8GB"
	},
	{
		title: "Disk Space",
		text: "200MB"
	},
	{
		title: "Monitor Resolution",
		text: "1920x1080"
	},
	{
		title: "System",
		text: "12.1 Monterey and above"
	}
];

const minimalRequirements = computed(() => {
	if (activeTab.value === "windows") {
		return windowsMinimal;
	} else {
		return macMinimal;
	}
});

const recommendedRequirements = computed(() => {
	if (activeTab.value === "windows") {
		return windowsReccommend;
	} else {
		return macReccommend;
	}
});
</script>

<style lang="scss" scoped>
.title{
	font-size: 36px;

	@include breakpoint(md) {
		font-size: 48px;
	}
}
.btn-inactive {
	background: rgba(255, 255, 255, 0.2);
}

.bg-gradient-blue-to-pink {
	background: var(
		--gradient,
		linear-gradient(
			73deg,
			rgba(0, 87, 255, 0.2) 4.01%,
			rgba(222, 89, 255, 0.2) 81.74%
		)
	);
}

.requirements-card {
	background: rgba(255, 255, 255, 0.07);
}

.list-enter-active,
.list-leave-active {
	transition: all 0.15s ease;
}
.list-enter-from,
.list-leave-to {
	opacity: 0;
	transform: translateY(-15px);
}
</style>
